import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useReward } from 'react-rewards';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PulsingDot } from '../components/ui/pulsing-dot';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../hooks/useAuth';
import { UserMenu } from '../components/UserMenu';
import { MilesLogo } from '../components/MilesLogo';
import { Link } from '@tanstack/react-router';

interface CheckoutMeta {
  type: 'subscription' | 'topup';
  previousPlanType?: string;
  previousCreditsPerMonth?: number;
  targetPlanType?: string;
  targetCreditsPerMonth?: number;
  credits?: number;
  previousCredits?: number;
  returnTo?: string;
}

function AnimatedCounter({
  from,
  to,
  duration = 1500,
  delay = 600,
}: {
  from: number;
  to: number;
  duration?: number;
  delay?: number;
}) {
  const [current, setCurrent] = useState(from);

  useEffect(() => {
    let rafId: number;
    const timer = setTimeout(() => {
      const start = performance.now();
      const animate = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCurrent(Math.round(from + (to - from) * eased));
        if (progress < 1) {
          rafId = requestAnimationFrame(animate);
        }
      };
      rafId = requestAnimationFrame(animate);
    }, delay);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(rafId);
    };
  }, [from, to, duration, delay]);

  return <>{current.toLocaleString()}</>;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function SuccessActions({
  primaryLabel,
  to,
  onNavigate,
}: {
  primaryLabel: string;
  to: string;
  onNavigate: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3">
      <Link to={to} onClick={onNavigate}>
        <Button variant="default" className="gap-2 px-6">
          {primaryLabel}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
      <Link
        to="/settings/billing"
        onClick={onNavigate}
        className="text-sm text-white/40 hover:text-white/60 transition-colors"
      >
        View billing
      </Link>
    </div>
  );
}

export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const { planType, plans, isLoading } = useSubscription();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const [thankYouLine] = useState(() => {
    const lines = [
      'Seriously, thank you!',
      'This means a lot, thank you!',
      'Thank you for believing in Miles!',
    ];
    return lines[Math.floor(Math.random() * lines.length)];
  });

  const [topUpLine] = useState(() => {
    const lines = [
      'Topped up and ready to build.',
      'Miles just got a boost.',
      'Fresh credits, fresh possibilities.',
    ];
    return lines[Math.floor(Math.random() * lines.length)];
  });

  const [meta] = useState<CheckoutMeta | null>(() => {
    const raw = localStorage.getItem('checkout_previous_plan');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed.type || !['subscription', 'topup'].includes(parsed.type)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  const clearMeta = () => localStorage.removeItem('checkout_previous_plan');

  const isSubscription = meta?.type === 'subscription';
  const isTopUp = meta?.type === 'topup';

  // For subscriptions: use meta's target plan if available, otherwise fall back
  // to what useSubscription returns (which updates once the webhook fires).
  const effectivePlanType =
    isSubscription && meta?.targetPlanType ? meta.targetPlanType : planType;
  const effectivePlan =
    plans.find((p) => p.type === effectivePlanType) ??
    plans.find((p) => p.type === planType);
  const effectiveCredits =
    isSubscription && meta?.targetCreditsPerMonth
      ? meta.targetCreditsPerMonth
      : effectivePlan?.creditsPerMonth;

  // Confetti
  const { reward } = useReward('confettiReward', 'confetti', {
    spread: 80,
    elementCount: 120,
    lifetime: 250,
  });
  const firedRef = useRef(false);

  useEffect(() => {
    if ((isSubscription || isTopUp) && !firedRef.current && !isLoading) {
      firedRef.current = true;
      const timer = setTimeout(() => reward(), 300);
      return () => clearTimeout(timer);
    }
  }, [isSubscription, isTopUp, isLoading, reward]);

  if (authLoading) {
    return (
      <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
        <div className="h-12 shrink-0" />
        <div className="miles-container flex-1 min-h-0 flex items-center justify-center">
          <PulsingDot />
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col px-4 pb-4 pt-2 gap-2">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <MilesLogo />
        <div className="flex items-center gap-2">
          {user && <UserMenu user={user} />}
        </div>
      </div>

      {/* Main content */}
      <div className="miles-container flex-1 min-h-0 flex items-center justify-center">
        <div className="max-w-xl w-full mx-auto px-6 text-center">
          {/* Confetti origin */}
          <span
            id="confettiReward"
            className="block mx-auto"
            style={{ height: 0 }}
          />

          {/* Subscription success */}
          {isSubscription && (
            <>
              <h1 className="text-3xl font-bold mb-1">
                {{
                  pro: 'You and Miles just went Pro.',
                  business: 'You and Miles are now in Business.',
                  agency: 'You and Miles are now on Agency.',
                }[effectivePlanType] ?? `Welcome to ${capitalize(effectivePlanType)}.`}
              </h1>
              <p className="text-lg text-white/40 mb-6">
                {thankYouLine}
              </p>
              <p className="text-white/50 mb-8">
                {{
                  pro: `Miles now has ${effectiveCredits?.toLocaleString() ?? '—'} credits a month to work with. Let's build something you're proud of.`,
                  business: `Miles now has serious room to work. ${effectiveCredits?.toLocaleString() ?? '—'} credits a month to build, iterate, and perfect across all your sites.`,
                  agency: `${effectiveCredits?.toLocaleString() ?? '—'} credits a month. Miles can juggle entire portfolios now. Build, iterate, and ship across every project.`,
                }[effectivePlanType] ??
                  `Miles now has ${effectiveCredits?.toLocaleString() ?? '—'} credits a month to work with.`}
              </p>

              <SuccessActions
                primaryLabel={meta?.returnTo ? 'Continue building' : 'Start building'}
                to={meta?.returnTo ?? '/'}
                onNavigate={clearMeta}
              />
            </>
          )}

          {/* Top-up success */}
          {isTopUp && (
            <>
              <h1 className="text-3xl font-bold mb-1">{topUpLine}</h1>
              <p className="text-lg text-white/40 mb-8">
                {meta?.credits
                  ? `${meta.credits.toLocaleString()} top-up credits added to your account!`
                  : 'Top-up credits added to your account!'}
              </p>

              {meta?.previousCredits != null && meta?.credits != null && (
                <div className="mb-8">
                  <div className="text-5xl font-bold tabular-nums">
                    <AnimatedCounter
                      from={meta.previousCredits}
                      to={meta.previousCredits + meta.credits}
                    />
                  </div>
                  <p className="text-sm text-white/40 mt-2">total top-up credits</p>
                </div>
              )}

              <SuccessActions
                primaryLabel="Continue building"
                to={meta?.returnTo ?? '/'}
                onNavigate={clearMeta}
              />
            </>
          )}

          {/* Fallback: no localStorage meta (direct navigation without checkout) */}
          {!meta && !isLoading && (
            <>
              <h1 className="text-3xl font-bold mb-1">
                Thanks for your purchase!
              </h1>
              <p className="text-lg text-white/40 mb-8">
                {thankYouLine}
              </p>

              <Link to="/">
                <Button variant="default" className="gap-2 px-6">
                  Start building
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </>
          )}

          {/* Loading state for fallback (no meta, still loading) */}
          {!meta && isLoading && (
            <PulsingDot className="justify-center" />
          )}
        </div>
      </div>
    </div>
  );
}
